import { BreathPattern } from '@/types';

export const BREATH_PATTERNS: BreathPattern[] = [
  {
    id: 'conversational',
    label: 'Conversational',
    description: 'Natural speech-rhythm breathing. Phrases land like you\'re telling a story to someone sitting across from you.',
    breathingCues: 'Natural pause points at commas and periods; mid-sentence breaths allowed; relaxed diaphragm; speak-singing delivery',
    physicsPrompt: `{BREATH-PHYSICS: CONVERSATIONAL}
[Breath_Behavior: Speech-pattern breathing; Natural pause exploitation; Relaxed diaphragm base]
[Breath_Phrasing: Story-telling rhythm; Comma-pause breaths; Intimate projection level]
[Breath_Character: Front-porch delivery; Unhurried; Words matter more than notes]`,
    sunoTags: ['conversational vocals', 'storytelling', 'intimate delivery', 'spoken-sung'],
    bestFor: ['Verses', 'Ballads', 'Story songs', 'Acoustic sets']
  },
  {
    id: 'athletic',
    label: 'Athletic',
    description: 'Deep diaphragm-locked breathing for maximum power and sustained delivery. Built for arena projection.',
    breathingCues: 'Deep belly breaths between phrases; locked diaphragm on sustained notes; controlled release; power reserve management',
    physicsPrompt: `{BREATH-PHYSICS: ATHLETIC}
[Breath_Behavior: Deep diaphragmatic lock; Power-reserve breath management; Controlled high-pressure release]
[Breath_Phrasing: Extended phrase capacity; Strategic breath placement; No audible gasping]
[Breath_Character: Arena-ready lung power; Athlete-grade endurance; Never sounds winded]`,
    sunoTags: ['powerful vocals', 'arena vocals', 'sustained power', 'athletic delivery'],
    bestFor: ['Choruses', 'Power ballads', 'Arena rock', 'Gospel crescendos']
  },
  {
    id: 'theatrical',
    label: 'Theatrical',
    description: 'Dramatic breath control with deliberate audible intakes used as expressive tools.',
    breathingCues: 'Audible dramatic inhales before big moments; held breath for tension; explosive release; pregnant pauses',
    physicsPrompt: `{BREATH-PHYSICS: THEATRICAL}
[Breath_Behavior: Dramatic audible inhales as expression; Tension-building breath holds; Explosive release dynamics]
[Breath_Phrasing: Broadway-scale dynamics; Deliberate silence exploitation; Maximum contrast]
[Breath_Character: Stage presence through breath; Audience-commanding pauses; Grand emotional gestures]`,
    sunoTags: ['dramatic vocals', 'theatrical delivery', 'expressive breathing'],
    bestFor: ['Bridges', 'Climactic moments', 'Ballads', 'Epic buildups']
  },
  {
    id: 'whisper-to-roar',
    label: 'Whisper-to-Roar',
    description: 'Extreme dynamic range breathing that transitions from barely-there whispers to full-throat explosions.',
    breathingCues: 'Minimal air for whisper sections; rapid diaphragm engagement for roar transitions; breath as volume controller',
    physicsPrompt: `{BREATH-PHYSICS: WHISPER-TO-ROAR}
[Breath_Behavior: Micro-breath whisper control; Rapid diaphragm shift for roar onset; Volume-through-air-pressure]
[Breath_Phrasing: Dynamic contrast within single phrases; Breath as expression controller]
[Breath_Character: Intimate-to-explosive range; Emotional ambush delivery; Unpredictable dynamics]`,
    sunoTags: ['dynamic vocals', 'whisper to scream', 'extreme dynamics', 'contrast vocals'],
    bestFor: ['Build sections', 'Emotional peaks', 'Pre-chorus to chorus transitions']
  },
  {
    id: 'staccato-punch',
    label: 'Staccato Punch',
    description: 'Sharp, punchy breath-separated delivery. Each word is a jab, each phrase a combination.',
    breathingCues: 'Quick micro-breaths between words; percussive diaphragm pulses; punching delivery; minimal sustain',
    physicsPrompt: `{BREATH-PHYSICS: STACCATO-PUNCH}
[Breath_Behavior: Micro-breath word separation; Percussive diaphragm punches; Rapid intake-release cycles]
[Breath_Phrasing: Boxing-rhythm delivery; Each syllable a separate strike; Rhythmic precision]
[Breath_Character: Machine-gun syllable delivery; Aggressive rhythmic authority; Relentless forward momentum]`,
    sunoTags: ['staccato vocals', 'punchy delivery', 'rhythmic vocals', 'aggressive phrasing'],
    bestFor: ['Rap-rock sections', 'Aggressive verses', 'Breakdowns', 'Punk passages']
  },
  {
    id: 'held-note',
    label: 'Held Note',
    description: 'Sustained breath control for long, drawn-out notes. The voice becomes an instrument holding a single tone.',
    breathingCues: 'Maximum lung fill; controlled slow release; vibrato on sustain; circular breathing awareness; minimal phrase breaks',
    physicsPrompt: `{BREATH-PHYSICS: HELD-NOTE}
[Breath_Behavior: Maximum lung capacity fill; Ultra-controlled slow air release; Sustained cord engagement]
[Breath_Phrasing: Extended single-note holds; Minimal phrase breaks; Drone-quality sustain]
[Breath_Character: Organ-pipe endurance; Meditation-level breath control; Hypnotic sustained power]`,
    sunoTags: ['sustained vocals', 'long notes', 'held notes', 'drone vocals'],
    bestFor: ['Doom passages', 'Atmospheric sections', 'Climactic holds', 'Outro sustains']
  },
  {
    id: 'circular-flow',
    label: 'Circular Flow',
    description: 'Seamless continuous delivery where breaths are hidden within the vocal flow. The voice never seems to stop.',
    breathingCues: 'Hidden breaths in consonant transitions; continuous vocal flow; overlapping phrase ends/starts; no audible intake',
    physicsPrompt: `{BREATH-PHYSICS: CIRCULAR-FLOW}
[Breath_Behavior: Hidden micro-breaths in consonant gaps; Continuous vocal stream; Overlapping phrase management]
[Breath_Phrasing: Seamless phrase connections; River-flow continuity; No audible breath breaks]
[Breath_Character: Perpetual motion delivery; Meditative continuous stream; Hypnotic unbroken flow]`,
    sunoTags: ['flowing vocals', 'continuous delivery', 'seamless phrasing', 'stream of consciousness'],
    bestFor: ['Jam sections', 'Psychedelic passages', 'Extended verses', 'Meditative sections']
  },
  {
    id: 'ragged-desperate',
    label: 'Ragged / Desperate',
    description: 'Audibly struggling for air. The voice sounds pushed past its limits — gasping, breaking, fighting to continue.',
    breathingCues: 'Audible gasping between phrases; breath-starved delivery; voice cracks from air depletion; desperate inhales',
    physicsPrompt: `{BREATH-PHYSICS: RAGGED-DESPERATE}
[Breath_Behavior: Audible desperate inhales; Breath-starved phonation; Air-depletion vocal breaks]
[Breath_Phrasing: Shortened phrases from exhaustion; Words cut short; Fighting for each syllable]
[Breath_Character: Raw survival energy; Beyond-the-limit desperation; Authenticity through struggle]`,
    sunoTags: ['desperate vocals', 'ragged breathing', 'raw vocals', 'exhausted delivery'],
    bestFor: ['Emotional breakdowns', 'Climactic desperation', 'Punk energy', 'Confessional moments']
  }
];

export function getBreathPatternById(id: string): BreathPattern | undefined {
  return BREATH_PATTERNS.find(b => b.id === id);
}
